import { SkyButton, SkyLoader, SkyMessageBar } from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import { Route, Switch, useLocation } from "wouter";
import type { ScheduleSummary } from "@shared/types/schedules";
import { TopbarActionsPortal } from "@/components/navigation/TopbarActionsPortal";
import { RunNowDialog } from "@/components/runs/RunNowDialog";
import { DeleteScheduleDialog } from "@/components/schedules/DeleteScheduleDialog";
import { SchedulesTable } from "@/components/schedules/SchedulesTable";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { usePermissions } from "@/hooks/use-permissions";
import { useInstanceConnections, useSchedules, useToggleSchedule } from "@/hooks/use-schedules";
import { failureMessage } from "@/lib/api";
import { AddIcon } from "@/lib/icons";
import { absolutePath } from "@/lib/routes";
import ScheduleEditorPage from "./schedules/editor";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { useLiveRunRefresh } from "@/hooks/use-live-run-refresh";
import { isTerminalRun } from "@/components/runs/runFormatting";
import { useReliability } from "@/hooks/use-reliability";
import type { ReliabilityWindow } from "@shared/types/reliability";

/**
 * Schedules (UX-3, FR-50).
 *
 * The route is declared with `nest` in `App.tsx`, so this page owns everything
 * under `/schedules`: the list at the root, the editor at `/new` and `/:id`.
 * The shell renders the page header for every route, so what is here is only
 * what sits below it.
 */

/** The notice after Enable or Disable: what the schedule does now. */
function toggledSentence(saved: ScheduleSummary, zone: string): string {
  if (saved.enabled) {
    if (saved.nextFireAt) {
      return `${saved.name} is enabled. Next import ${formatInstant(saved.nextFireAt, zone, "short", { named: true })}.`;
    }
    return `${saved.name} is enabled.`;
  }
  return `${saved.name} is disabled. Nothing more is scheduled. An import already in progress finishes.`;
}

function SchedulesList() {
  const zone = useDisplayTz();
  const { canAny } = usePermissions();
  const notice = useNotice();
  const showApiError = useApiErrorNotice();
  const [, navigate] = useLocation();

  const schedules = useSchedules();
  const connections = useInstanceConnections();
  const toggleSchedule = useToggleSchedule();

  const [runTarget, setRunTarget] = useState<ScheduleSummary | null>(null);
  // #1016: Delete opens a confirmation first; nothing is deleted until it is confirmed.
  const [deleteTarget, setDeleteTarget] = useState<ScheduleSummary | null>(null);
  // #828 (Level B): the success rate over 7 days, or 30 on the toggle.
  const [successWindow, setSuccessWindow] = useState<ReliabilityWindow>("7d");
  const reliability = useReliability(successWindow);
  // #2005: a schedule's last outcome and Success follow a run that starts or finishes while the
  // page is open: the page holds the tab's live feed, whose run frames refresh the reliability
  // read (#1653). That read is also polled in case no frame comes: every 15 s while a last
  // outcome reads queued or running and the socket is not live, every 30 s otherwise.
  useLiveRunRefresh({
    active: (reliability.data?.schedules ?? []).some(
      (row) => row.lastOutcome !== null && !isTerminalRun(row.lastOutcome),
    ),
    poll: () => void reliability.refetch(),
  });

  const canWrite = canAny("schedules.write");
  const canDelete = canAny("schedules.delete");
  const canStartRun = canAny("runs.start");
  const canReadRuns = canAny("runs.read");

  const setEnabled = async (schedule: ScheduleSummary, enabled: boolean): Promise<void> => {
    try {
      const saved = await toggleSchedule.mutateAsync({ id: schedule.id, enabled });
      // One sentence under the page title (the toast's `content` hid its message). A run
      // already in progress is not stopped by disabling its schedule.
      notice.success(toggledSentence(saved, zone));
    } catch (error) {
      showApiError(error, "The schedule could not be changed.");
    }
  };

  if (schedules.isLoading) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <SkyLoader size="medium" label="Loading schedules…" labelPosition="below" />
      </div>
    );
  }

  // A failed first read replaces the page; a failed re-read keeps the rows on screen and says so
  // below (#1398), as the Runs list does.
  if (schedules.isError && !schedules.data) {
    return (
      <SkyMessageBar
        intent="error"
        layout="multiline"
        title="We could not load schedules"
        message={failureMessage(schedules.error, "Try again.")}
        actions={
          <SkyButton size="small" onClick={() => void schedules.refetch()}>
            Try again
          </SkyButton>
        }
        testId="schedules-error"
      />
    );
  }

  const rows = schedules.data ?? [];

  return (
    <>
      <TopbarActionsPortal>
        {/* No instance-wide Run now here (walk 2026-10-02, D15): #214 moved it to Connections;
            each schedule's row menu keeps its own. */}
        {canWrite ? (
          <SkyButton
            appearance="primary"
            icon={AddIcon}
            onClick={() => navigate(absolutePath("/schedules/new"))}
            testId="schedule-new"
          >
            New schedule
          </SkyButton>
        ) : null}
      </TopbarActionsPortal>

      {schedules.isError ? (
        <div className="mb-3">
          <SkyMessageBar
            intent="warning"
            layout="multiline"
            title="We could not refresh the schedules"
            message={`${failureMessage(schedules.error, "Try again.")} The schedules on screen are still here.`}
            actions={
              <SkyButton size="small" onClick={() => void schedules.refetch()}>
                Try again
              </SkyButton>
            }
            testId="schedules-refresh-error"
          />
        </div>
      ) : null}

      <SchedulesTable
        schedules={rows}
        isLoading={schedules.isFetching}
        canWrite={canWrite}
        canDelete={canDelete}
        canStartRun={canStartRun}
        canReadRuns={canReadRuns}
        onRunNow={setRunTarget}
        onDelete={setDeleteTarget}
        onSetEnabled={(schedule, enabled) => void setEnabled(schedule, enabled)}
        reliability={
          canReadRuns
            ? { data: reliability.data, window: successWindow, onWindowChange: setSuccessWindow }
            : undefined
        }
      />

      {canDelete ? (
        <DeleteScheduleDialog target={deleteTarget} onClose={() => setDeleteTarget(null)} />
      ) : null}

      {/* Mounted only while open, so the target it was opened with is the
          target it runs. */}
      {canStartRun && runTarget ? (
        <RunNowDialog
          open
          onOpenChange={(open) => {
            if (!open) setRunTarget(null);
          }}
          connections={connections.data ?? []}
          schedules={rows}
          initialTarget={{ kind: "schedule", id: runTarget.id }}
        />
      ) : null}
    </>
  );
}

export default function SchedulesPage() {
  // Each editor is keyed by the record it edits (walk 2026-10-02, D3): `/new` and `/:id` render
  // the same component at the same place, so without a key React kept one schedule's draft when
  // the app moved to New schedule, or to another schedule.
  return (
    <Switch>
      <Route path="/new">
        <ScheduleEditorPage key="new" />
      </Route>
      <Route path="/:id">
        {(params) => <ScheduleEditorPage key={params.id} scheduleId={params.id} />}
      </Route>
      <Route path="/">
        <SchedulesList />
      </Route>
    </Switch>
  );
}
